import React from "react";
import { Link } from "react-router-dom";
import { BedDouble, Maximize, MapPin, Zap } from "lucide-react";
import { formatPrice } from "../lib/api";
import { useT } from "../lib/i18n";
import { cn } from "../lib/utils";
import { SourceBadge } from "./SourceBadge";

export function matchColor(score) {
  if (score >= 80) return { bg: "bg-success", text: "text-white" };
  if (score >= 60) return { bg: "bg-warning", text: "text-white" };
  return { bg: "bg-slate-400", text: "text-white" };
}

export const MatchPill = ({ score, className }) => {
  const c = matchColor(score);
  return (
    <span
      className={cn("inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold", c.bg, c.text, className)}
      data-testid="match-pill"
    >
      {score}% match
    </span>
  );
};

export const ListingCard = ({ listing }) => {
  const { t } = useT();
  const img = listing.image || (listing.gallery && listing.gallery[0]);

  return (
    <Link
      to={`/woning/${listing.id}`}
      data-testid={`listing-card-${listing.id}`}
      className="group flex flex-col overflow-hidden rounded-3xl border border-border bg-white shadow-sm transition-all hover:border-brandblue hover:shadow-xl sm:flex-row"
    >
      <div className="relative aspect-[4/3] w-full shrink-0 overflow-hidden bg-slate-100 sm:aspect-auto sm:w-[42%] sm:min-h-[260px] sm:max-w-[420px]">
        {img ? (
          <img
            src={img}
            alt={listing.title}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full flex-col items-center justify-center bg-gradient-to-br from-brandblue-light via-slate-100 to-amber-50 text-brandblue">
            <MapPin size={44} className="opacity-60" />
            <span className="mt-2 px-4 text-center text-xs font-semibold text-navy/70">
              {listing.city || "Nederland"}
            </span>
          </div>
        )}
        <div className="absolute left-3 top-3 flex gap-2">
          {listing.is_live ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-success px-2.5 py-1 text-xs font-bold text-white">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" />
              {t("common.live")}
            </span>
          ) : (
            <span className="rounded-full bg-white/90 px-2.5 py-1 text-xs font-semibold text-navy">
              {t("common.soon")}
            </span>
          )}
        </div>
        {typeof listing.match_score === "number" && (
          <div className="absolute right-3 top-3">
            <MatchPill score={listing.match_score} />
          </div>
        )}
      </div>

      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-display text-lg font-extrabold text-navy sm:text-xl">{listing.title}</h3>
            <p className="mt-1 flex items-center gap-1 truncate text-sm text-slate-500">
              <MapPin size={13} /> {listing.street}, {listing.city}
            </p>
          </div>
          <div className="shrink-0 text-right">
            <div className="font-display text-xl font-extrabold text-navy sm:text-2xl">{formatPrice(listing.price)}</div>
            <div className="text-xs text-slate-400">{t("common.perMonth")}</div>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap gap-2 text-xs font-medium text-slate-600">
          <span className="inline-flex items-center gap-1 rounded-full bg-slate-50 px-2.5 py-1">
            <BedDouble size={13} /> {listing.bedrooms} {t("common.beds")}
          </span>
          <span className="inline-flex items-center gap-1 rounded-full bg-slate-50 px-2.5 py-1">
            <Maximize size={13} /> {listing.m2} m²
          </span>
          {listing.energy_label && (
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-50 px-2.5 py-1">
              <Zap size={13} /> {listing.energy_label}
            </span>
          )}
        </div>

        {listing.description && (
          <p className="mt-3 line-clamp-2 text-sm text-slate-500 sm:line-clamp-3">
            {listing.description}
          </p>
        )}

        <div className="mt-auto flex items-center justify-between gap-3 border-t border-border pt-3">
          <SourceBadge source={listing.source} />
          <span className="text-xs font-semibold text-brandblue group-hover:underline">Bekijk →</span>
        </div>
      </div>
    </Link>
  );
};

export const ListingCardSkeleton = () => (
  <div className="flex flex-col overflow-hidden rounded-3xl border border-border bg-white sm:flex-row">
    <div className="aspect-[4/3] w-full shrink-0 animate-pulse bg-slate-100 sm:aspect-auto sm:min-h-[260px] sm:w-[42%] sm:max-w-[420px]" />
    <div className="flex-1 space-y-3 p-5">
      <div className="h-5 w-2/3 animate-pulse rounded bg-slate-100" />
      <div className="h-3 w-1/2 animate-pulse rounded bg-slate-100" />
      <div className="flex gap-2">
        <div className="h-6 w-16 animate-pulse rounded-full bg-slate-100" />
        <div className="h-6 w-16 animate-pulse rounded-full bg-slate-100" />
      </div>
      <div className="h-3 w-5/6 animate-pulse rounded bg-slate-100" />
      <div className="h-3 w-4/6 animate-pulse rounded bg-slate-100" />
    </div>
  </div>
);
